<template>
  <v-list dense style="min-width: 0; max-width: 100%;">
    <v-subheader>
      {{ displayTitle }}
      <v-icon x-small class="align-center" style="margin-left: 4px;" color="primary" @click.stop="infoDialog = true">mdi-information-outline</v-icon>
        <v-dialog v-model="infoDialog" max-width="600px" open-on-hover>
          <v-card>
            <v-card-title class="headline">
              {{ $t('components.ExternalSeriesTable.spreadsheetTips') }}
            </v-card-title>
            <v-card-text>
              <ol>
                <li>{{ $t('components.ExternalSeriesTable.tip1') }}</li>
                <li>{{ $t('components.ExternalSeriesTable.tip2') }} <b :style="`color: ${primaryColor}`">{{ $t('components.ExternalSeriesTable.thisColor') }}</b></li>
                <li>{{ $t('components.ExternalSeriesTable.tip3') }}</li>
                <li>{{ $t('components.ExternalSeriesTable.tip4') }}</li>
              </ol>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="red darken-1"
                text
                @click="infoDialog = false"
              >
                {{ $t('components.ExternalSeriesTable.close') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
    </v-subheader>
    <div class="series-scroll-host" :class="{ 'series-scroll-host--clipped': gridOwnsScroll }">
      <spreadsheet :key="displayTitle.concat(columns.toString())" v-model="data" :columns="columns" :min-max-by-row="minMaxByRow" :width="fullWidth" :custom-styles="customStyles" :default-values="originalData" class="mt-2" @input="updatedSpreadsheet" @overflow="gridOwnsScroll = $event" />
    </div>
    <div v-if="length > 1 && this.showChart">
      <channel-curves-chart-multi
        :data="dataObj"
        :animation="false"
        :show-legend="true"
        :x-name="$t('components.ExternalSeriesTable.period')"
        :y-name="yUnits"
        :hide-title="true"
        legend-orient="vertical"
        :export="false"
        :show-negatives="true"
        :scale="true"
        :x-axis-type-is-category="true"
        :x-axis-type-is-date="true"
        :x-axis-rotate="60"
        :grid-bottom-margin="50" />
    </div>
  </v-list>
</template>

<script>
import numbro from 'numbro'
import moment from 'moment'
import Spreadsheet from '@/components/ekrm/Spreadsheet.vue'
import ChannelCurvesChartMulti from '@/components/simulationDetails/ChannelCurvesChartMulti.vue'
export default {
  data: function () {
    return {
      data: [],
      externalSeriesChanges: [],
      infoDialog: false,
      gridOwnsScroll: false,
      primaryColor: this.$vuetify.theme.themes.light.primary,
      // Also store changes by date. This is needed to maintain consistency when the date range changes and there are user-entered values
      changesByDate: {}
    }
  },
  components: {
    Spreadsheet,
    ChannelCurvesChartMulti
  },
  computed: {
    displayTitle () {
      return this.title || this.$t('components.ExternalSeriesTable.externalVariables')
    },
    displaySeriesHeader () {
      return this.seriesHeader || this.$t('components.ExternalSeriesTable.series')
    },
    yUnits () {
      const units = this.series.map(s => s[1].unit)
      if (units.length > 0 && units.every(value => value === units[0])) {
        return `Value (${units[0]})`
      } else {
        return this.$t('components.ExternalSeriesTable.valueVariousUnits')
      }
    },
    minMaxByRow () {
      return this.series.map(series => {
        return [series[1].min, series[1].max]
      })
    },
    fullWidth () {
      return '100%'
    },
    columns: function () {
      let cols = [{ type: 'html', title: this.displaySeriesHeader + (this.showMinMax ? ' [min-max]' : ''), width: '250px', readOnly: true, wordWrap: true, align: 'left' }]
      return cols.concat(
        this.dates.slice(this.start, this.start + this.length).map(period => {
          return { type: 'numeric', title: this.formatDate(period), width: '100px', decimal: '.', allowEmpty: true, allowNegatives: true }
        })
      )
    },
    originalData: function () {
      return this.series.map(s => {
        return [`<h4><b>${this.prettyNames && this.prettyNames[s[0]] ? this.prettyNames[s[0]] : s[0]}</b> ${this.showUnits && s[1].unit ? '(' + s[1].unit + ')' : ''}</h4>` + (this.showMinMax ? `<h5 style="color:lightgray;">[${this.formatValue(s[1].min)} - ${this.formatValue(s[1].max)}]</h5>` : '')].concat(s[1].default_values.slice(this.start, this.start + this.length))
      })
    },
    dataObj: function () {
      let seriesObj = {}
      this.series.forEach((s, seriesIndex) => {
        const seriesKey = s[0]
        const seriesName = `${this.prettyNames && this.prettyNames[seriesKey] ? this.prettyNames[seriesKey] : seriesKey} ${this.showUnits && s[1].unit ? '(' + s[1].unit + ')' : ''}`

        const yValues = []
        for (let i = 0; i < this.length; i++) {
          const dateIndex = this.start + i
          const date = this.dates[dateIndex]

          if (this.changesByDate[seriesKey] && this.changesByDate[seriesKey][date] !== undefined && this.changesByDate[seriesKey][date] !== null) {
            yValues.push(this.changesByDate[seriesKey][date])
          } else {
            yValues.push(s[1].default_values[dateIndex])
          }
        }

        seriesObj[seriesName] = {
          y: yValues,
          x: this.dates.slice(this.start, this.start + this.length)
        }
      })
      return seriesObj
    },
    originalDataWithChanges: function () {
      return this.originalData.map((s, index) => {
        const key = this.series[index][0]
        let t = [s[0]]

        if (this.changesByDate[key]) {
          for (let i = 1; i < s.length; i++) {
            const dateIndex = this.start + i - 1
            const date = this.dates[dateIndex]
            if (this.changesByDate[key][date] !== undefined && this.changesByDate[key][date] !== null) {
              t[i] = this.changesByDate[key][date]
            } else {
              t[i] = s[i]
            }
          }
        } else {
          t = [...s]
        }
        return t
      })
    },
    customStyles: function () {
      // Trigger when variable changes
      // eslint-disable-next-line
      this.externalSeriesChanges || this.originalDataWithChanges
      let result = {}
      for (let i = 0; i < this.data.length; i++) {
        for (let j = 1; j < this.data[i].length; j++) {
          const key = this.getColumnName(j) + (i + 1)
          if (this.data[i][j].toString() !== this.originalData[i][j].toString()) {
            result[key] = `font-weight: bold; color:${this.$vuetify.theme.themes.light.primary};`
          } else {
            result[key] = `font-weight: normal; color:black;`
          }
        }
      }
      return result
    }
  },
  methods: {
    getColumnName: function (colIndex) {
      // Generate Excel-style column names: A, B, ..., Z, AA, AB, ...
      let result = ''
      let index = colIndex
      while (index >= 0) {
        result = String.fromCharCode(65 + (index % 26)) + result
        index = Math.floor(index / 26) - 1
      }
      return result
    },
    updatedSpreadsheet: function () {
      if (!this.series || this.series.length === 0 || !this.data || this.data.length === 0) {
        return
      }
      if (this.data.length !== this.series.length) {
        return
      }

      // Create a new object to ensure reactivity
      const newChangesByDate = {}

      this.data.forEach((row, seriesIndex) => {
        const seriesKey = this.series[seriesIndex][0]
        newChangesByDate[seriesKey] = {}

        const [, ...values] = row
        values.forEach((value, colIndex) => {
          const dateIndex = this.start + colIndex
          const date = this.dates[dateIndex]

          // Always track the actual value from spreadsheet, not just changes
          newChangesByDate[seriesKey][date] = Number(value)
        })

        // Copy over any existing changes for dates outside the current view
        if (this.changesByDate[seriesKey]) {
          for (const date in this.changesByDate[seriesKey]) {
            if (!newChangesByDate[seriesKey].hasOwnProperty(date)) {
              newChangesByDate[seriesKey][date] = this.changesByDate[seriesKey][date]
            }
          }
        }
      })

      // Replace the entire object to ensure Vue detects the change
      this.changesByDate = newChangesByDate

      const outputObject = {}
      // Include all series, not just those with changes
      for (const series of this.series) {
        const seriesKey = series[0]
        const seriesData = series[1]

        // Check if there are any changes within the current view range
        const changesInRange = {}
        if (this.changesByDate[seriesKey]) {
          for (const date in this.changesByDate[seriesKey]) {
            const dateIndex = this.dates.indexOf(date)
            // Only include changes that fall within the current view (start to start + length)
            if (dateIndex >= this.start && dateIndex < this.start + this.length) {
              changesInRange[date] = this.changesByDate[seriesKey][date]
            }
          }
        }

        // Create array with actual spreadsheet values for the current view range
        const rangeArray = []
        for (let i = 0; i < this.length; i++) {
          const dateIndex = this.start + i
          const date = this.dates[dateIndex]

          // Use the actual spreadsheet value (now always tracked in changesInRange)
          if (changesInRange[date] !== undefined) {
            rangeArray.push(changesInRange[date])
          } else {
            // Fallback to default if somehow not tracked
            rangeArray.push(seriesData.default_values[dateIndex])
          }
        }
        outputObject[seriesKey] = rangeArray
      }

      this.externalSeriesChanges = outputObject
      this.$emit('input', outputObject)
    },
    formatValue: function (value) {
      return numbro(value).format({
        average: true,
        lowPrecision: false,
        mantissa: 1,
        optionalMantissa: true
      })
    },
    formatDate (date) {
      if (this.modelFrequency === 'month') {
        return moment(date).format('MMM \'YY')
      } else {
        return moment(date).format('YYYY-MM-DD')
      }
    },
    dataMerge (original, modified) {
      let merged = JSON.parse(JSON.stringify(original)) // Deep copy

      // Apply changes from changesByDate
      for (let i = 0; i < this.series.length; i++) {
        const seriesKey = this.series[i][0]
        if (this.changesByDate[seriesKey]) {
          for (let j = 1; j < merged[i].length; j++) {
            const dateIndex = this.start + j - 1
            const date = this.dates[dateIndex]

            if (this.changesByDate[seriesKey][date] !== undefined) {
              merged[i][j] = this.changesByDate[seriesKey][date]
            }
          }
        }
      }

      return merged
    },
    syncChangesByDateFromValue () {
      if (this.value && Object.keys(this.value).length > 0) {
        const newChangesByDate = {}
        for (const seriesKey in this.value) {
          newChangesByDate[seriesKey] = {}
          this.value[seriesKey].forEach((value, index) => {
            if (value !== null && value !== undefined) {
              // Calculate the actual date index based on start position
              const dateIndex = this.start + index
              if (this.dates[dateIndex]) {
                const date = this.dates[dateIndex]
                newChangesByDate[seriesKey][date] = value
              }
            }
          })
        }
        this.changesByDate = newChangesByDate
        // Trigger data update after syncing
        this.data = this.dataMerge(this.originalData, this.data)
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (newVal) {
        // Only sync if changesByDate is empty (initial load)
        if (Object.keys(this.changesByDate).length === 0) {
          this.syncChangesByDateFromValue()
        }
      }
    },
    originalData: {
      immediate: true,
      handler () {
        // Always use dataMerge to preserve changes
        this.data = this.dataMerge(this.originalData, this.data)
      }
    },
    length: {
      immediate: false,
      handler () {
        this.updatedSpreadsheet()
      }
    }
  },
  created () {
    // Ensure changesByDate is populated on component creation
    this.syncChangesByDateFromValue()
  },
  props: {
    value: {
      type: Object
    },
    series: {
      type: Array,
      required: true
    },
    dates: {
      type: Array,
      required: true
    },
    start: {
      type: Number,
      required: false,
      default: function () {
        return 0
      }
    },
    length: {
      type: Number,
      required: false,
      default: function () {
        return this.dates.length
      }
    },
    modelFrequency: {
      type: String,
      required: false,
      default: function () {
        return 'month'
      }
    },
    title: {
      type: String,
      required: false,
      default: ''
    },
    seriesHeader: {
      type: String,
      required: false,
      default: ''
    },
    showMinMax: {
      type: Boolean,
      required: false,
      default () {
        return true
      }
    },
    showUnits: {
      type: Boolean,
      required: false,
      default () {
        return true
      }
    },
    showChart: {
      type: Boolean,
      required: false,
      default () {
        return true
      }
    },
    prettyNames: {
      type: Object,
      required: false,
      default () {
        return {}
      }
    }
  }
}
</script>

<style scoped>
/* Keeps the spreadsheet from stretching the v-col/v-list flex chain past the
   screen (the reason this wrapper exists) without becoming a second horizontal
   scroller next to the grid's own one. While the grid reports that it owns the
   scroll, its .jexcel_content is guaranteed to be the scroller and to be
   showing a bar, so there is nothing left here to scroll to and this element
   only needs to clip: overflow-x: hidden keeps the block formatting context but
   never paints a bar. macOS hid the duplicate anyway (overlay scrollbars are
   not painted at rest), which is why it only ever showed up on Linux/Windows.
   If that state never arrives the wrapper stays overflow-x: auto and scrolls as
   before, so the fallback is one bar here rather than two or none. */
.series-scroll-host {
  overflow-x: auto;
  max-width: 100%;
}
.series-scroll-host--clipped {
  overflow-x: hidden;
}
</style>
